import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Activity, AlertCircle, Filter, RefreshCw } from "lucide-react";
import { API_URL } from "../utils/api";

const API = API_URL;

const PROVIDER_LABEL = {
  openai: "OpenAI",
  anthropic: "Claude",
};

const PROVIDER_BADGE_COLORS = {
  openai: "bg-emerald-100 text-emerald-800",
  anthropic: "bg-purple-100 text-purple-800",
};

const FALLBACK_REASON_LABEL = {
  none: "Primary OK",
  primary_error_or_timeout: "Primary timeout/error",
  primary_empty: "Primary tomt svar",
  primary_invalid_output: "Primary ogiltigt svar",
  unknown: "Okänd",
};

// Same pattern as OptimizationCenter: no credentials flag, no Bearer.
// On production (elido.nu) `API_URL=""` so requests are same-origin and
// the httpOnly auth cookie is sent automatically by the browser. On
// preview (separate frontend/backend domains) the same code path works
// because preview env actually shares the auth cookie via CORS rules.
const FETCH_OPTS = {};

function formatTimestamp(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatMs(ms) {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

function StatCell({ label, value, sub, tone = "default" }) {
  const toneCls = {
    default: "text-slate-900",
    good: "text-emerald-700",
    warn: "text-amber-700",
    bad: "text-red-700",
  }[tone];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className={`text-lg font-semibold ${toneCls}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ClientStats({ window, agg }) {
  if (!agg || agg.total === 0) {
    return (
      <div className="text-xs text-slate-400 italic">
        Inga LLM-anrop på {window}.
      </div>
    );
  }
  const fbTone = agg.fallback_rate_pct >= 20 ? "bad" : agg.fallback_rate_pct >= 5 ? "warn" : "good";
  const errTone = agg.error_rate_pct >= 5 ? "bad" : agg.error_rate_pct >= 1 ? "warn" : "good";
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
      <StatCell label="Anrop" value={agg.total} sub={window} />
      <StatCell
        label="OpenAI"
        value={`${agg.openai_pct}%`}
        tone="good"
      />
      <StatCell
        label="Claude"
        value={`${agg.anthropic_pct}%`}
        tone={agg.anthropic_pct > 30 ? "warn" : "default"}
      />
      <StatCell
        label="Fallback-rate"
        value={`${agg.fallback_rate_pct}%`}
        tone={fbTone}
      />
      <StatCell
        label="Error-rate"
        value={`${agg.error_rate_pct}%`}
        tone={errTone}
      />
      <StatCell
        label="Avg / p95"
        value={`${formatMs(agg.avg_total_llm_ms)}`}
        sub={`p95: ${formatMs(agg.p95_total_llm_ms)}`}
      />
    </div>
  );
}

export default function LlmDiagnostics() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retentionDays, setRetentionDays] = useState(14);
  const [filter, setFilter] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/llm-diagnostics/clients`, FETCH_OPTS);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setClients(data.clients || []);
      setRetentionDays(data.retention_days || 14);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (clientId) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await fetch(
        `${API}/api/admin/llm-diagnostics/clients/${encodeURIComponent(clientId)}`,
        FETCH_OPTS
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDetail(await res.json());
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  useEffect(() => {
    if (selectedClient) fetchDetail(selectedClient);
  }, [selectedClient, fetchDetail]);

  const filtered = filter
    ? clients.filter((c) =>
        (c.name || "").toLowerCase().includes(filter.toLowerCase())
        || (c.client_id || "").toLowerCase().includes(filter.toLowerCase())
      )
    : clients;

  if (!user) return null;

  return (
    <div className="space-y-6" data-testid="admin-llm-diagnostics">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-[#255E69]" />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">LLM-diagnostik</h1>
            <p className="text-xs text-slate-500">
              Routing-statistik per klient. Endast teknisk metadata — inga
              meddelanden eller PII sparas. Retention: {retentionDays} dagar.
            </p>
          </div>
        </div>
        <button
          onClick={fetchClients}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          data-testid="llm-diag-refresh"
        >
          <RefreshCw className="w-4 h-4" />
          Uppdatera
        </button>
      </div>

      <div className="relative max-w-md">
        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtrera på klientnamn eller ID…"
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#255E69]/30"
          data-testid="llm-diag-filter"
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 italic">Laddar…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-slate-500 italic" data-testid="llm-diag-empty">
          Inga LLM-anrop loggade ännu (retention {retentionDays} dagar).
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <button
              key={c.client_id}
              onClick={() => setSelectedClient(c.client_id)}
              className={`block w-full text-left bg-white border rounded-lg p-4 transition-colors hover:border-[#255E69]/40 ${
                selectedClient === c.client_id ? "border-[#255E69]/60 ring-1 ring-[#255E69]/20" : "border-slate-200"
              }`}
              data-testid="llm-diag-client-row"
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  <div className="text-[10px] text-slate-400 font-mono">{c.client_id}</div>
                </div>
                <div className="text-xs text-slate-500">
                  {c.last_24h.total} anrop senaste 24h
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Senaste 24 timmar</div>
                  <ClientStats window="24h" agg={c.last_24h} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1 mt-2">Senaste 7 dagar</div>
                  <ClientStats window="7d" agg={c.last_7d} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedClient && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-6" data-testid="llm-diag-detail">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">
              Senaste 20 events {detail?.name ? `— ${detail.name}` : ""}
            </h2>
            <button
              onClick={() => { setSelectedClient(null); setDetail(null); }}
              className="text-xs text-slate-500 hover:text-slate-900"
              data-testid="llm-diag-detail-close"
            >
              Stäng
            </button>
          </div>
          {detailLoading ? (
            <div className="text-sm text-slate-500 italic">Laddar…</div>
          ) : !detail || detail.last_events.length === 0 ? (
            <div className="text-sm text-slate-500 italic">Inga events.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500 uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-2 pr-3">Tid</th>
                    <th className="text-left py-2 pr-3">Provider</th>
                    <th className="text-left py-2 pr-3">Modell</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-right py-2 pr-3">Provider-tid</th>
                    <th className="text-right py-2 pr-3">Total</th>
                    <th className="text-left py-2 pr-3">Session</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  {detail.last_events.map((e, i) => (
                    <tr key={i} className="border-t border-slate-200" data-testid="llm-diag-event-row">
                      <td className="py-2 pr-3 whitespace-nowrap">{formatTimestamp(e.created_at)}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          PROVIDER_BADGE_COLORS[e.used_provider] || "bg-slate-100 text-slate-700"
                        }`}>
                          {PROVIDER_LABEL[e.used_provider] || e.used_provider || "—"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 font-mono text-[10px]">{e.used_model || "—"}</td>
                      <td className="py-2 pr-3">
                        {e.success ? (
                          <span className="text-emerald-700">
                            {FALLBACK_REASON_LABEL[e.fallback_reason] || e.fallback_reason}
                          </span>
                        ) : (
                          <span className="text-red-700">
                            Båda misslyckades ({FALLBACK_REASON_LABEL[e.fallback_reason] || e.fallback_reason})
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{formatMs(e.used_duration_ms)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{formatMs(e.total_llm_ms)}</td>
                      <td className="py-2 pr-3 font-mono text-[10px] text-slate-400 truncate max-w-[180px]">
                        {e.session_id || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
